import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import { symbolData, BLANK_SYMBOL_ID } from "../../../SymbolData";
import { Settings, GAME_LAYOUT } from "../../../settings";
import { COIN_SYMBOL_IDS } from "../../../SymbolData";
import { formatToCustomLocale } from "../../../utilities";
import { soundManager } from "../../../SoundManager";
import { SOUND_NAME } from "../../../sounds";
import { FEATURES_CONFIG } from "../../../featuresConfig";
import SpineAtlasUploader from "../../../utils/SpineAtlasUploader";

// Manages overlay animations for winning symbols with pre-positioned spine instances

class OverlayReelController {
  constructor(
    overlaySymbolRefs,
    reelIndex,
    symbolWidth,
    symbolHeight,
    reelContainer = null,
    bet = 1,
  ) {
    this.overlaySymbolRefs = overlaySymbolRefs;
    this.reelIndex = reelIndex;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.reelContainer = reelContainer;
    this.bet = bet || 1; // Store bet value for coin calculations
    this.prePositionedSpines = new Map();
    this.activeAnimations = new Map();
    this.animationCache = new Map();

    // ✅ NEW: cache of available animations per spineKey
    this.animationAvailability = new Map();

    // Internal caches for performance optimization
    this._spineScaleCache = new Map(); // Cache for calculateSpineScale results
    this._expandingWildScaleCache = null; // Cache for expanding wild scale
    this._lastSymbolWidth = symbolWidth;
    this._lastSymbolHeight = symbolHeight;
    this._coinDisplayValueCache = new Map(); // Cache for coin display values

    // Expanding wild full-reel spine properties
    this.expandingWildSpine = null;
    this.expandingWildSpineData = {
      skeleton: "wild_expand-skeleton",
      atlas: "wild_expand-atlas",
    };
    this.expandingWildTimeouts = [];
    this.isExpandingWildVisible = false;
    this.expandingWildInitialized = false;
    this.currentExpandingWildAnimation = null; // Track current animation ("appear", "idle", or "win")

    // Spine scaling configuration
    this.expandingWildHeightMultiplier = 1.0;
    this.expandingWildYOffsetMultiplier = 0;

    // Coin value text management (attached to spine bone)
    this.coinValueContainers = new Map(); // symbolIndex → { counterContainer, bitmapTexts, displayValue }

    // Track pending requestAnimationFrame handles for cleanup
    this._pendingRAF = null;

    this.initializeAllSpineInstances();

    // Initialize and warm up expanding wild spine on Reel 0 only (shader compilation is global)
    // This prevents first-time RAF warnings when expanding wild appears
    if (this.reelIndex === 0 && this.reelContainer) {
      this.initializeExpandingWildSpine();
    }
  }

  getShadowReelController() {
    return this;
  }

  /**
   * Get the expanding wild spine instance for event listening
   * @returns {Spine|null} The expanding wild spine instance
   */
  getExpandingWildSpine() {
    return this.expandingWildSpine;
  }

  /**
   * Calculate display value for coin symbols (coinValue × bet)
   * @param {number} symbolId - Symbol ID
   * @param {number|null} betOverride - Optional bet value for error recovery (ONE TIME use)
   */
  _getCoinDisplayValue(symbolId, betOverride = null) {
    const symbolValue = symbolData[symbolId]?.value;
    if (!symbolValue || symbolValue === null) {
      return "";
    }

    try {
      const coinValue = parseInt(symbolValue, 10);

      if (isNaN(coinValue) || coinValue <= 0) {
        return "";
      }

      // Use bet override if provided (error recovery), otherwise use this.bet
      const bet = betOverride !== null ? betOverride : this.bet;

      const displayValue = coinValue * bet;
      return formatToCustomLocale(displayValue);
    } catch (error) {
      return "";
    }
  }

  /**
   * Create coin value text container with BitmapText
   * Implements progressive scaling based on digit count:
   * - 1-4 digits: Normal size (no scaling)
   * - 5-8 digits: Horizontal squishing only (preserve height)
   * - 9+ digits: Uniform scaling (both width and height)
   */
  createCoinValueText(displayValue) {
    if (!displayValue || displayValue === "") {
      return null;
    }

    const counterContainer = new PIXI.Container();
    counterContainer.name = "coin_value_text";
    counterContainer.alpha = 1;
    counterContainer.zIndex = 1000; // Render above spine effects

    // Calculate font size based on current symbol height (same as ReelControllerV2).
    // The ticker counteracts the spine's PIXI scale, so this fontSize produces
    // identical visual results to the texture-based text.
    const fontSize =
      this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;

    // Create BitmapText for the value (target native size: 34px)
    const bitmapText = new PIXI.BitmapText(displayValue, {
      fontName: "font_secondary_scaled",
      fontSize: fontSize,
    });

    bitmapText.anchor.set(0.5);
    bitmapText.x = 0;
    bitmapText.y = 0;
    bitmapText.zIndex = 1;
    bitmapText.roundPixels = true;

    // Force update to get accurate measurements
    bitmapText.updateText();

    // Calculate available width (with padding)
    const coinWidth = this.symbolWidth;
    const paddingPercent = 0.12; // 12% padding on each side
    const maxTextWidth = coinWidth * (1 - paddingPercent * 2);

    const actualTextWidth = bitmapText.width;

    // If text is too wide, scale down uniformly to fit (preserves aspect ratio)
    if (actualTextWidth > maxTextWidth) {
      const scale = maxTextWidth / actualTextWidth;
      bitmapText.scale.set(scale);
    }

    counterContainer.addChild(bitmapText);

    return { counterContainer, bitmapTexts: [bitmapText] };
  }

  /**
   * Show coin value text for a coin symbol
   * @param {number} symbolIndex - Position index
   * @param {number} symbolId - Symbol ID
   * @param {object} spineInstance - Spine instance
   * @param {number|null} betOverride - Optional bet value for error recovery
   */
  showCoinValueText(symbolIndex, symbolId, spineInstance, betOverride = null) {
    // Skip entirely when coins don't appear on base game reels
    if (!FEATURES_CONFIG.SPIN.COIN_VALUES_ON_REELS) return;

    // Check if it's a coin symbol
    if (!COIN_SYMBOL_IDS.includes(symbolId)) {
      return;
    }

    // Check if coin value text already exists for this symbol at this position
    const existingCoinData = this.coinValueContainers.get(symbolIndex);
    if (
      existingCoinData &&
      existingCoinData.counterContainer &&
      !existingCoinData.counterContainer.destroyed
    ) {
      // Coin value text already exists, don't recreate it
      return;
    }

    // Hide existing coin value text if any (cleanup destroyed containers)
    this.hideCoinValueText(symbolIndex);

    // Calculate display value (use bet override for recovery, otherwise current bet)
    const displayValue = this._getCoinDisplayValue(symbolId, betOverride);
    if (!displayValue || displayValue === "") {
      return;
    }

    // Create coin value text
    const textData = this.createCoinValueText(displayValue);
    if (!textData) {
      return;
    }

    const { counterContainer, bitmapTexts } = textData;

    // Store reference with spine instance for proper cleanup
    this.coinValueContainers.set(symbolIndex, {
      counterContainer,
      bitmapTexts,
      spineInstance, // Needed for removeSlotObject on cleanup
      displayValue, // Store the value so it never changes during animation
    });

    // Attach to the animated txt_slot_value slot.
    // Spine-pixi's updateSlotObject overwrites the slot object's entire
    // localTransform matrix with the bone's world transform (bone.a, bone.d)
    // every frame. Two scale factors shrink the text:
    //   1. spine.scale.x (PIXI scale to fit symbol in grid) — e.g. 0.405
    //   2. bone.a (bone's world transform scale) — e.g. ~0.5
    // We counter both by scaling fontSize up by 1/(spineScale * boneBaseScale).
    // The bone's animated scale change (idle pulse 1.0→1.1) still applies
    // naturally on top, giving the text its desired pulse/win effects.
    if (spineInstance) {
      const spineScale = spineInstance.scale.x || 1;

      // Force world transform update so bone values are computed (they're 0 before first frame)
      spineInstance.update(0);
      const valueBone = spineInstance.skeleton?.findBone("value");
      const boneBaseScale = Math.abs(valueBone?.a) || 1;

      // Total counter-scale: undo both spine PIXI scale and bone world scale
      const totalScale = spineScale * boneBaseScale;
      if (totalScale && totalScale !== 0) {
        bitmapTexts.forEach((bt) => {
          if (bt && !bt.destroyed) {
            bt.fontSize = bt.fontSize / totalScale;
            bt.updateText();
          }
        });

        // Re-check auto-fit with adjusted measurements (in spine-local units)
        const coinWidth = this.symbolWidth;
        const paddingPercent = 0.12;
        const maxTextWidth = (coinWidth * (1 - paddingPercent * 2)) / totalScale;
        bitmapTexts.forEach((bt) => {
          if (bt && !bt.destroyed) {
            bt.scale.set(1); // Reset before measuring
            bt.updateText();
            if (bt.width > maxTextWidth) {
              bt.scale.set(maxTextWidth / bt.width);
            }
          }
        });
      }

      spineInstance.addSlotObject("txt_slot_value", counterContainer);

      // Store spine reference for cleanup
      this.coinValueContainers.get(symbolIndex).spineInstance = spineInstance;
    }
  }

  /**
   * Hide coin value text for a symbol
   */
  hideCoinValueText(symbolIndex) {
    const coinData = this.coinValueContainers.get(symbolIndex);
    if (!coinData) {
      return;
    }

    // Detach from spine slot BEFORE destroying (prevents localTransform error)
    if (coinData.spineInstance && coinData.counterContainer && !coinData.counterContainer.destroyed) {
      try {
        coinData.spineInstance.removeSlotObject("txt_slot_value", coinData.counterContainer);
      } catch {
        // Silent — spine may already be destroyed
      }
    }

    // Destroy bitmap texts
    if (coinData.bitmapTexts) {
      coinData.bitmapTexts.forEach((text) => {
        if (text && !text.destroyed) {
          text.destroy();
        }
      });
    }

    // Destroy container
    if (coinData.counterContainer && !coinData.counterContainer.destroyed) {
      if (coinData.counterContainer.parent) {
        coinData.counterContainer.parent.removeChild(coinData.counterContainer);
      }
      coinData.counterContainer.destroy({ children: true });
    }

    // Remove from map
    this.coinValueContainers.delete(symbolIndex);
  }

  /**
   * Hide all coin value texts
   */
  hideAllCoinValueTexts() {
    const symbolIndices = Array.from(this.coinValueContainers.keys());
    symbolIndices.forEach((symbolIndex) => {
      this.hideCoinValueText(symbolIndex);
    });
  }

  /**
   * Update bet value and refresh all visible coin value texts
   */
  updateBet(newBet) {
    const oldBet = this.bet;
    this.bet = newBet || 1; // Store new bet value for next spin

    // Invalidate coin display value cache when bet changes
    if (oldBet !== this.bet) {
      this._coinDisplayValueCache.clear();
    }

    // Don't update coin values on reels - they belong to a completed spin round
    // Coin values will update when new symbols land on next spin
    // Note: Paytable/info overlay update separately (not affected by this)
    return;
  }

  /**
   * ✅ Combined and optimized method for checking available animations.
   * Returns object with hasLanding, hasIdle, and hasAppear using cached data.
   */
  getAvailableAnimations(symbolIndex, symbolId) {
    // Texture-only game optimization - skip landing/idle checks if disabled
    if (
      !FEATURES_CONFIG.SPINE_ANIMATIONS.LANDING_ENABLED &&
      !FEATURES_CONFIG.SPINE_ANIMATIONS.IDLE_ENABLED
    ) {
      return { hasLanding: false, hasIdle: false, hasAppear: true };
    }

    // Always look up using position 0 since all positions share the same symbol's animations
    const warmupKey = `0_${symbolId}`;
    const available = this.animationAvailability.get(warmupKey);

    if (!available) {
      return { hasLanding: false, hasIdle: false, hasAppear: false };
    }

    let hasLanding = false;
    let hasIdle = false;
    let hasAppear = false;

    for (const name of available) {
      if (!hasLanding && (name.includes("land") || name === "in")) hasLanding = true;
      // Treat "static" as an idle-equivalent (e.g. jackpot spine j.json has no "idle" but has "static")
      if (!hasIdle && (name.includes("idle") || name === "static")) hasIdle = true;
      if (!hasAppear && name.includes("appear")) hasAppear = true;
      if (hasLanding && hasIdle && hasAppear) break;
    }

    // Apply feature flags to results
    return {
      hasLanding:
        FEATURES_CONFIG.SPINE_ANIMATIONS.LANDING_ENABLED && hasLanding,
      hasIdle: FEATURES_CONFIG.SPINE_ANIMATIONS.IDLE_ENABLED && hasIdle,
      hasAppear,
    };
  }

  // ✅ hasIdleAnimationAt now uses cached availability instead of scanning animations
  hasIdleAnimationAt(symbolIndex, symbolId) {
    const { hasIdle } = this.getAvailableAnimations(symbolIndex, symbolId);
    return hasIdle;
  }

  // Show win animation using pre-positioned spine instances.
  // If the spine has no actual win animation (e.g. Zeus bonus symbols), falls back
  // to idle so the symbol still looks alive rather than playing a wrong/fallback clip.
  // @param {number|null} betOverride - Optional bet value for error recovery
  // @param {number} targetLoops - Number of loops (0=infinite, 1=play once and hold, N=play N times and hold)
  showWinAnimation(
    symbolIndex,
    symbolId,
    animationType = "win",
    betOverride = null,
    targetLoops = 0,
  ) {
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (!overlaySymbol) return;

    const spineKey = `${symbolIndex}_${symbolId}`;
    const spineInstance = this.prePositionedSpines.get(spineKey);
    if (!spineInstance) return;

    const existingAnimation = this.activeAnimations.get(symbolIndex);

    // Early return if SAME symbol and animation type already playing
    // Prevents flicker on shared symbols across multiple winning lines during cycling
    if (
      existingAnimation &&
      existingAnimation.symbolId === symbolId &&
      existingAnimation.animationType === animationType
    ) {
      // Symbol already playing - don't restart! Just ensure visibility
      spineInstance.alpha = 1;
      overlaySymbol.alpha = 1;
      return;
    }

    // Also skip restart when existing is "idle" from a win-to-idle fallback
    // and the incoming request would also produce the same fallback.
    // Without this, symbols without a dedicated win clip restart every cycle
    // while symbols with win clips stay stable (dedup above catches them).
    if (
      existingAnimation &&
      existingAnimation.symbolId === symbolId &&
      existingAnimation.animationType === "idle" &&
      (animationType === "win" || animationType === "win_fast")
    ) {
      // Check if this request would also fall back to idle
      const candidateAnim = this.selectSpineAnimation(spineInstance, animationType, symbolId);
      if (candidateAnim && !candidateAnim.toLowerCase().includes("win")) {
        // Same fallback — don't restart, just ensure visibility
        spineInstance.alpha = 1;
        overlaySymbol.alpha = 1;
        return;
      }
    }

    // Only hide if DIFFERENT symbol or animation type
    if (existingAnimation) {
      this.hideWinAnimation(symbolIndex);
    }

    // Always reset bones (clears stale scale/position from prior animations).
    // Only reset slots when hidden (preserves inherited attachments when visible).
    if (spineInstance.skeleton) {
      spineInstance.skeleton.setBonesToSetupPose();
      if (spineInstance.alpha < 0.5) {
        spineInstance.skeleton.setSlotsToSetupPose();
      }
    }

    let selectedAnimation = this.selectSpineAnimation(
      spineInstance,
      animationType,
      symbolId,
    );

    // Win-to-idle fallback: if we asked for "win" but the resolved animation
    // doesn't actually contain "win" (selectSpineAnimation fell back to a
    // generic clip), play idle instead so the symbol still looks correct.
    // This handles Zeus bonus symbols whose spines have no dedicated win clip.
    let effectiveAnimationType = animationType;
    if (
      animationType === "win" &&
      selectedAnimation &&
      !selectedAnimation.toLowerCase().includes("win")
    ) {
      const idleAnimation = this.selectSpineAnimation(spineInstance, "idle", symbolId);
      if (idleAnimation) {
        selectedAnimation = idleAnimation;
        effectiveAnimationType = "idle";
      }
    }



    const shouldLoop = targetLoops === 0 || targetLoops > 1;
    if (spineInstance.state && selectedAnimation) {
      spineInstance.state.clearTracks();
      const entry = spineInstance.state.addAnimation(0, selectedAnimation, shouldLoop, 0);

      // For finite loops > 1, count completions and stop looping at target
      if (targetLoops > 1 && entry) {
        let completedLoops = 0;
        entry.listener = {
          complete: () => {
            completedLoops++;
            if (completedLoops >= targetLoops) {
              entry.loop = false; // Hold on last frame
            }
          },
        };
      }
    }

    // Make visible AFTER animation is queued — prevents stale-pose flash.
    // Force first frame to apply so the skeleton pose matches the new animation
    // before any pixel renders. Without this, setup/stale pose shows for 1 frame.
    spineInstance.state?.update(0);
    spineInstance.state?.apply(spineInstance.skeleton);
    spineInstance.alpha = 1;
    spineInstance.autoUpdate = true;
    overlaySymbol.alpha = 1;

    this.activeAnimations.set(symbolIndex, {
      spineInstance,
      symbolId,
      animationType: effectiveAnimationType,
    });

    // Show coin value text if this is a coin symbol (pass bet override for recovery)
    this.showCoinValueText(symbolIndex, symbolId, spineInstance, betOverride);
  }


  /**
   * Sync/restart win animation to frame 0 without hiding (keeps animations in sync across lines).
   * If the spine has no actual win animation, falls back to idle (same logic as showWinAnimation).
   * Unlike showWinAnimation, this ALWAYS restarts even if same animation is playing.
   * @param {number} targetLoops - Number of loops (0=infinite, 1=play once and hold, N=play N times and hold)
   */
  syncWinAnimation(
    symbolIndex,
    symbolId,
    animationType = "win",
    betOverride = null,
    targetLoops = 0,
  ) {
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (!overlaySymbol) return;

    const spineKey = `${symbolIndex}_${symbolId}`;
    const spineInstance = this.prePositionedSpines.get(spineKey);
    if (!spineInstance) return;

    // Reset bones before starting new animation (clears stale scale from prior anim)
    if (spineInstance.skeleton?.setBonesToSetupPose) {
      spineInstance.skeleton.setBonesToSetupPose();
    }

    // Ensure visibility
    spineInstance.alpha = 1;
    spineInstance.autoUpdate = true;
    overlaySymbol.alpha = 1;

    let selectedAnimation = this.selectSpineAnimation(
      spineInstance,
      animationType,
      symbolId,
    );

    // Win-to-idle fallback: same logic as showWinAnimation.
    // If selectSpineAnimation fell back to a non-win clip, play idle instead.
    let effectiveAnimationType = animationType;
    if (
      animationType === "win" &&
      selectedAnimation &&
      !selectedAnimation.toLowerCase().includes("win")
    ) {
      const idleAnimation = this.selectSpineAnimation(spineInstance, "idle", symbolId);
      if (idleAnimation) {
        selectedAnimation = idleAnimation;
        effectiveAnimationType = "idle";
      }
    }

    const shouldLoop = targetLoops === 0 || targetLoops > 1;

    // Reset animation to frame 0 without hiding
    if (spineInstance.state && selectedAnimation) {
      // Get current track (if exists)
      const currentTrack = spineInstance.state.tracks[0];

      // If already playing the same animation, just reset time
      if (currentTrack && currentTrack.animation.name === selectedAnimation) {
        currentTrack.trackTime = 0;
        currentTrack.loop = shouldLoop;
        // Re-attach loop control listener for finite loops
        if (targetLoops > 1) {
          let completedLoops = 0;
          currentTrack.listener = {
            complete: () => {
              completedLoops++;
              if (completedLoops >= targetLoops) {
                currentTrack.loop = false;
              }
            },
          };
        } else {
          currentTrack.listener = null;
        }
      } else {
        // Different animation, need to switch
        spineInstance.state.clearTracks();
        const entry = spineInstance.state.addAnimation(0, selectedAnimation, shouldLoop, 0);

        if (targetLoops > 1 && entry) {
          let completedLoops = 0;
          entry.listener = {
            complete: () => {
              completedLoops++;
              if (completedLoops >= targetLoops) {
                entry.loop = false;
              }
            },
          };
        }
      }
    }

    this.activeAnimations.set(symbolIndex, {
      spineInstance,
      symbolId,
      animationType: effectiveAnimationType,
    });

    // Show coin value text if this is a coin symbol
    this.showCoinValueText(symbolIndex, symbolId, spineInstance, betOverride);
  }

  // ✅ Show landing animation using cached availability
  // @param {number|null} betOverride - Optional bet value for error recovery
  showLandingAnimation(symbolIndex, symbolId, betOverride = null) {
    const spineKey = `${symbolIndex}_${symbolId}`;

    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (!overlaySymbol) {
      return;
    }

    const spineInstance = this.prePositionedSpines.get(spineKey);
    if (!spineInstance) {
      return;
    }

    const { hasLanding, hasIdle } = this.getAvailableAnimations(
      symbolIndex,
      symbolId,
    );

    if (!hasLanding && !hasIdle) {
      return;
    }

    const existingAnimation = this.activeAnimations.get(symbolIndex);
    if (existingAnimation && existingAnimation.animationType === "win") {
      this.hideWinAnimation(symbolIndex);
    }

    // Always reset bones (clears stale scale/position from prior animations).
    // Only reset slots when hidden (preserves inherited attachments when visible).
    if (spineInstance.skeleton) {
      spineInstance.skeleton.setBonesToSetupPose();
      if (spineInstance.alpha < 0.5) {
        spineInstance.skeleton.setSlotsToSetupPose();
      }
    }

    if (hasLanding && hasIdle) {
      const landingAnimation = this.selectSpineAnimation(
        spineInstance,
        "land",
        symbolId,
      );
      const idleAnimation = this.selectSpineAnimation(
        spineInstance,
        "idle",
        symbolId,
      );

      if (spineInstance.state && landingAnimation && idleAnimation) {
        spineInstance.state.clearTracks();
        spineInstance.state.addAnimation(0, landingAnimation, false, 0);
        // Round-level idle: landing holds on last frame; idle triggers after ALL reels stop
        if (!FEATURES_CONFIG.SPINE_ANIMATIONS.ROUND_LEVEL_IDLE) {
          spineInstance.state.addAnimation(0, idleAnimation, true, 0);
        } else {

        }
      }
    } else if (hasIdle) {
      // Round-level idle: idle-only symbols stay as static texture until round-level trigger
      if (FEATURES_CONFIG.SPINE_ANIMATIONS.ROUND_LEVEL_IDLE) {

        // Reset spine visibility — don't activate the overlay
        spineInstance.alpha = 0.001;
        spineInstance.autoUpdate = false;
        overlaySymbol.alpha = 0;
        return;
      }
      const idleAnimation = this.selectSpineAnimation(
        spineInstance,
        "idle",
        symbolId,
      );
      if (spineInstance.state && idleAnimation) {
        spineInstance.state.clearTracks();
        spineInstance.state.addAnimation(0, idleAnimation, true, 0);
      }
    } else if (hasLanding) {
      const landingAnimation = this.selectSpineAnimation(
        spineInstance,
        "land",
        symbolId,
      );
      if (spineInstance.state && landingAnimation) {
        spineInstance.state.clearTracks();
        spineInstance.state.addAnimation(0, landingAnimation, true, 0);
        if (FEATURES_CONFIG.SPINE_ANIMATIONS.ROUND_LEVEL_IDLE) {

        }
      }
    }

    // Make visible AFTER animation is queued — prevents stale-pose flash.
    // Force first frame — use same time step (1/60) as texture extraction
    // so the overlay's initial pose exactly matches the static texture.
    spineInstance.state?.update(1 / 60);
    spineInstance.state?.apply(spineInstance.skeleton);
    spineInstance.alpha = 1;
    spineInstance.autoUpdate = true;
    overlaySymbol.alpha = 1;


    // When round-level idle is active and we have landing, record as "landing" (not "idle")
    // so the post-spin logic knows landing is still holding and can trigger idle explicitly
    const isRoundLevelHolding = FEATURES_CONFIG.SPINE_ANIMATIONS.ROUND_LEVEL_IDLE && hasLanding;
    const recordedType = isRoundLevelHolding
      ? "landing"
      : hasLanding && hasIdle ? "idle" : hasIdle ? "idle" : "landing";

    this.activeAnimations.set(symbolIndex, {
      spineInstance,
      symbolId,
      animationType: recordedType,
      hadLandingAnimation: hasLanding,
    });

    // Show coin value text if this is a coin symbol (pass bet override for recovery)
    this.showCoinValueText(symbolIndex, symbolId, spineInstance, betOverride);
  }

  /**
   * Show landing animation followed by win (instead of idle).
   * Used for Zeus bonus symbols — they play land→win during POT charging.
   * @param {number} symbolIndex
   * @param {number} symbolId
   * @param {number|null} betOverride
   */
  showLandingToWinAnimation(symbolIndex, symbolId, betOverride = null) {
    const spineKey = `${symbolIndex}_${symbolId}`;
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (!overlaySymbol) return;

    const spineInstance = this.prePositionedSpines.get(spineKey);
    if (!spineInstance) return;

    const { hasLanding } = this.getAvailableAnimations(symbolIndex, symbolId);

    const existingAnimation = this.activeAnimations.get(symbolIndex);
    if (existingAnimation && existingAnimation.animationType === "win") {
      this.hideWinAnimation(symbolIndex);
    }

    // Always reset bones (clears stale scale/position from prior animations).
    // Only reset slots when hidden (preserves inherited attachments when visible).
    if (spineInstance.skeleton) {
      spineInstance.skeleton.setBonesToSetupPose();
      if (spineInstance.alpha < 0.5) {
        spineInstance.skeleton.setSlotsToSetupPose();
      }
    }

    const winAnimation = this.selectSpineAnimation(spineInstance, "win", symbolId);

    if (hasLanding && winAnimation) {
      const landingAnimation = this.selectSpineAnimation(spineInstance, "land", symbolId);
      if (spineInstance.state && landingAnimation) {
        spineInstance.state.clearTracks();
        spineInstance.state.addAnimation(0, landingAnimation, false, 0);
        spineInstance.state.addAnimation(0, winAnimation, true, 0);
      }
    } else if (winAnimation) {
      if (spineInstance.state) {
        spineInstance.state.clearTracks();
        spineInstance.state.addAnimation(0, winAnimation, true, 0);
      }
    }

    // Make visible AFTER animation is queued
    spineInstance.state?.update(0);
    spineInstance.state?.apply(spineInstance.skeleton);
    spineInstance.alpha = 1;
    spineInstance.autoUpdate = true;
    overlaySymbol.alpha = 1;

    this.activeAnimations.set(symbolIndex, {
      spineInstance,
      symbolId,
      animationType: "win",
    });

    this.showCoinValueText(symbolIndex, symbolId, spineInstance, betOverride);
  }

  // Show idle animation (unchanged, uses cache internally now)
  // @param {number|null} betOverride - Optional bet value for error recovery
  // @param {boolean} frozen - If true, sets up idle pose but doesn't animate (autoUpdate=false).
  //   Used during per-reel landing so the swap from texture→spine happens during the
  //   reel bounce (invisible). Call unfreezeIdleAnimations() later to start playing.
  showIdleAnimation(symbolIndex, symbolId, betOverride = null, frozen = false) {
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (!overlaySymbol) return;

    const spineKey = `${symbolIndex}_${symbolId}`;
    const spineInstance = this.prePositionedSpines.get(spineKey);
    if (!spineInstance) return;

    const { hasIdle } = this.getAvailableAnimations(symbolIndex, symbolId);
    if (!hasIdle) return;

    // If the same idle is already playing and spine is visible, don't restart.
    // This prevents phase-reset flicker when multiple callers trigger idle on
    // the same symbol in the same frame.
    const existingAnimation = this.activeAnimations.get(symbolIndex);
    if (
      existingAnimation &&
      existingAnimation.symbolId === symbolId &&
      existingAnimation.animationType === "idle" &&
      spineInstance.alpha >= 0.5
    ) {
      // If already showing frozen and caller wants unfrozen, just unfreeze
      if (!frozen && !spineInstance.autoUpdate) {
        spineInstance.autoUpdate = true;
      }
      return;
    }

    // Reset bone transforms (scale, position, rotation) to clear stale values
    // from prior animations (e.g. win animation may scale the symbol).
    // Always reset bones — idle doesn't key every bone so stale transforms carry over.
    // Only reset SLOTS when hidden — when visible (e.g. landing → idle),
    // slot attachments like shadows must be preserved (idle inherits them).
    // Skip on first-ever use: spine is already in setup pose.
    const wasPreviouslyUsed = existingAnimation != null;
    if (wasPreviouslyUsed && spineInstance.skeleton) {
      spineInstance.skeleton.setBonesToSetupPose();
      if (spineInstance.alpha < 0.5) {
        spineInstance.skeleton.setSlotsToSetupPose();
      }
    }

    const idleAnimation = this.selectSpineAnimation(
      spineInstance,
      "idle",
      symbolId,
    );
    if (spineInstance.state && idleAnimation) {
      spineInstance.state.clearTracks();
      spineInstance.state.setAnimation(0, idleAnimation, true);
    }

    // Make visible AFTER animation is queued — prevents stale-pose flash.
    // Force first frame to apply so the skeleton pose matches the new animation
    // before any pixel renders. Without this, setup/stale pose shows for 1 frame.
    spineInstance.state?.update(0);
    spineInstance.state?.apply(spineInstance.skeleton);
    // When frozen (autoUpdate=false), the ticker won't call update(), so mesh
    // vertices would never be recomputed. Force a zero-dt update now so the
    // static pose renders correctly.
    if (frozen) {
      spineInstance.autoUpdate = false;
      spineInstance.update(0);
    }
    spineInstance.alpha = 1;
    spineInstance.autoUpdate = !frozen;
    overlaySymbol.alpha = 1;

    this.activeAnimations.set(symbolIndex, {
      spineInstance,
      symbolId,
      animationType: "idle",
    });

    // Show coin value text if this is a coin symbol (pass bet override for recovery)
    this.showCoinValueText(symbolIndex, symbolId, spineInstance, betOverride);
  }

  /**
   * Unfreezes all frozen idle animations on this reel.
   * Called when all reels have stopped to start the idle movement.
   */
  unfreezeIdleAnimations() {
    for (const [, entry] of this.activeAnimations) {
      if (entry.animationType === "idle" && entry.spineInstance) {
        entry.spineInstance.autoUpdate = true;
      }
    }
  }

  /**
   * Smoothly crossfade from the current animation (e.g. win) to idle.
   * Uses setAnimation with a mix duration so Spine blends poses — no snap.
   * Forces frame 0 via update(0)+apply() so all symbols start in phase.
   */
  transitionToIdle(symbolIndex, symbolId) {
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (!overlaySymbol) return;

    const spineKey = `${symbolIndex}_${symbolId}`;
    const spineInstance = this.prePositionedSpines.get(spineKey);
    if (!spineInstance) return;

    const { hasIdle } = this.getAvailableAnimations(symbolIndex, symbolId);
    if (!hasIdle) return;

    // Ensure visible and updating
    spineInstance.alpha = 1;
    spineInstance.autoUpdate = true;
    overlaySymbol.alpha = 1;

    const idleAnimation = this.selectSpineAnimation(
      spineInstance,
      "idle",
      symbolId,
    );
    if (spineInstance.state && idleAnimation) {
      // Full clean reset — clearTracks removes stale animation data that Spine's
      // mix system would otherwise blend from (causing a visible bump).
      spineInstance.state.clearTracks();
      if (spineInstance.skeleton?.setToSetupPose) {
        spineInstance.skeleton.setToSetupPose();
      }
      spineInstance.state.setAnimation(0, idleAnimation, true);
      spineInstance.state.update(0);
      spineInstance.state.apply(spineInstance.skeleton);
    }

    this.activeAnimations.set(symbolIndex, {
      spineInstance,
      symbolId,
      animationType: "idle",
    });
  }

  // Show appear animation (used when symbol first appears, may fallback to idle or win)
  // @param {number|null} betOverride - Optional bet value for error recovery
  showAppearAnimation(symbolIndex, symbolId, betOverride = null) {
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (!overlaySymbol) return;

    const spineKey = `${symbolIndex}_${symbolId}`;
    const spineInstance = this.prePositionedSpines.get(spineKey);
    if (!spineInstance) return;

    const { hasAppear, hasIdle, hasLanding } = this.getAvailableAnimations(
      symbolIndex,
      symbolId,
    );

    // If no animations available, don't show anything
    if (!hasAppear && !hasIdle && !hasLanding) return;

    // Always reset bones (clears stale scale/position from prior animations).
    // Only reset slots when hidden (preserves inherited attachments when visible).
    if (spineInstance.skeleton) {
      spineInstance.skeleton.setBonesToSetupPose();
      if (spineInstance.alpha < 0.5) {
        spineInstance.skeleton.setSlotsToSetupPose();
      }
    }

    let selectedAnimation = null;

    // Try appear first, fallback to idle, then landing
    if (hasAppear) {
      selectedAnimation = this.selectSpineAnimation(
        spineInstance,
        "appear",
        symbolId,
      );
    } else if (hasIdle) {
      selectedAnimation = this.selectSpineAnimation(
        spineInstance,
        "idle",
        symbolId,
      );
    } else if (hasLanding) {
      selectedAnimation = this.selectSpineAnimation(
        spineInstance,
        "land",
        symbolId,
      );
    }

    if (spineInstance.state && selectedAnimation) {
      spineInstance.state.clearTracks();
      spineInstance.state.addAnimation(0, selectedAnimation, true, 0);
    }

    // Make visible AFTER animation is queued
    spineInstance.state?.update(0);
    spineInstance.state?.apply(spineInstance.skeleton);
    spineInstance.alpha = 1;
    spineInstance.autoUpdate = true;
    overlaySymbol.alpha = 1;

    this.activeAnimations.set(symbolIndex, {
      spineInstance,
      symbolId,
      animationType: "appear",
    });

    // Show coin value text if this is a coin symbol (pass bet override for recovery)
    this.showCoinValueText(symbolIndex, symbolId, spineInstance, betOverride);
  }

  hideWinAnimation(symbolIndex) {
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    const animationData = this.activeAnimations.get(symbolIndex);
    if (!animationData || !overlaySymbol) return;

    const { spineInstance } = animationData;
    spineInstance.alpha = 0.001;
    spineInstance.autoUpdate = false;
    overlaySymbol.alpha = 0.001;

    // Spine is invisible (alpha 0.001) — safe to do a full reset.
    // Reset bones AND slots so the spine is pristine for the next animation.
    // Without this, bone transforms (scale/position) from the win animation
    // persist and corrupt the next animation shown on this spine.
    if (spineInstance.state) {
      spineInstance.state.clearTracks();
    }
    if (spineInstance.skeleton?.setToSetupPose) {
      spineInstance.skeleton.setToSetupPose();
    }

    this.activeAnimations.delete(symbolIndex);

    // Hide coin value text if any
    this.hideCoinValueText(symbolIndex);
  }

  hideLandingAnimation(symbolIndex) {
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    const animationData = this.activeAnimations.get(symbolIndex);
    if (!animationData || !overlaySymbol) return;

    if (animationData.animationType !== "landing") return;

    const { spineInstance } = animationData;
    spineInstance.alpha = 0.001;
    spineInstance.autoUpdate = false;
    overlaySymbol.alpha = 0.001;

    // Spine is invisible (alpha 0.001) — safe to do a full reset.
    if (spineInstance.state) {
      spineInstance.state.clearTracks();
    }
    if (spineInstance.skeleton?.setToSetupPose) {
      spineInstance.skeleton.setToSetupPose();
    }

    this.activeAnimations.delete(symbolIndex);
  }

  hideAllAnimations(exceptPositions = null) {
    // First: hide tracked animations (the fast path)
    const activeIndexes = Array.from(this.activeAnimations.keys());
    activeIndexes.forEach((symbolIndex) => {
      if (exceptPositions && exceptPositions.has(symbolIndex)) {
        return;
      }
      this.hideWinAnimation(symbolIndex);
    });

    // Hide all coin value texts (they should already be hidden by hideWinAnimation, but ensure cleanup)
    this.hideAllCoinValueTexts();

    // CRITICAL: Full sweep of ALL pre-positioned spines to catch orphaned autoUpdate=true
    // Without this, spines that lost their activeAnimations entry (race conditions in async win flow)
    // remain ticking on every frame forever, causing gradual CPU accumulation in turbo mode
    if (!exceptPositions) {
      for (const spine of this.prePositionedSpines.values()) {
        if (spine && spine.autoUpdate) {
          spine.autoUpdate = false;
          spine.alpha = 0.001;
          if (spine.state) {
            spine.state.clearTracks();
          }
        }
      }
      this.activeAnimations.clear();
    }
  }

  hideAllAnimationsExcept(upcomingPositions = []) {
    if (!upcomingPositions || upcomingPositions.length === 0) {
      this.hideAllAnimations();
      return;
    }

    // Skip if no animations are active on this reel
    if (this.activeAnimations.size === 0) return;

    const preserveMap = new Map();
    for (let i = 0; i < upcomingPositions.length; i++) {
      const [reelIndex, symbolIndex, symbolId] = upcomingPositions[i];
      if (reelIndex === this.reelIndex) {
        preserveMap.set(symbolIndex, symbolId);
      }
    }

    for (const [symbolIndex, animationData] of this.activeAnimations) {
      const preservedSymbolId = preserveMap.get(symbolIndex);
      if (preservedSymbolId !== animationData.symbolId) {
        this.hideWinAnimation(symbolIndex);
      }
    }
  }

  calculateSpineScale(
    spineInstance,
    containerWidth,
    containerHeight,
    skipAnimationSetup = false,
    symbolId = null,
  ) {
    if (
      !skipAnimationSetup &&
      spineInstance &&
      spineInstance.state &&
      spineInstance.state.data &&
      spineInstance.state.data.skeletonData.animations.length > 0
    ) {
      const defaultAnimation =
        spineInstance.state.data.skeletonData.animations[0];
      spineInstance.state.addAnimation(0, defaultAnimation.name, true, 0);
    }

    // Per-category scale — single lookup with DEFAULT fallback
    const info = symbolId != null ? symbolData[symbolId] : null;
    const symbolPay = info?.symbolPay || '';
    const categoryScale = Settings.SYMBOL_SCALE_OVERRIDES?.[symbolPay]
      ?? Settings.SYMBOL_SCALE_OVERRIDES?.DEFAULT
      ?? 1;

    // Check cache
    const cacheKey = `${containerWidth}_${containerHeight}_${symbolPay}`;
    if (this._spineScaleCache.has(cacheKey)) {
      return this._spineScaleCache.get(cacheKey);
    }

    const nativeWidth = Settings.SYMBOL_WIDTH;
    const nativeHeight = Settings.SYMBOL_HEIGHT;
    const scale = (containerHeight / nativeHeight) * categoryScale;

    const scaledWidth = nativeWidth * scale;
    const scaledHeight = nativeHeight * scale;
    // No centering offset needed: the sprite uses anchor(0.5) at position (0,0),
    // and the spine's origin (0,0) is its design center. Both align at the
    // container origin by default. A non-zero offset here would create a visible
    // shift between the static texture and the overlay spine during transitions.
    const x = 0;
    const y = 0;

    const result = {
      scale,
      nativeWidth,
      nativeHeight,
      x,
      y,
      scaledWidth,
      scaledHeight,
    };

    // Cache result
    this._spineScaleCache.set(cacheKey, result);

    return result;
  }

  resizeAnimations(newSymbolWidth, newSymbolHeight) {
    if (
      this.symbolWidth === newSymbolWidth &&
      this.symbolHeight === newSymbolHeight
    ) {
      return;
    }

    this.symbolWidth = newSymbolWidth;
    this.symbolHeight = newSymbolHeight;

    // Invalidate caches when dimensions change
    this._spineScaleCache.clear();
    this._expandingWildScaleCache = null;
    this._coinDisplayValueCache.clear(); // Clear coin cache on resize (bet might have changed)
    this._lastSymbolWidth = newSymbolWidth;
    this._lastSymbolHeight = newSymbolHeight;

    const nativeHeight = Settings.SYMBOL_HEIGHT;

    for (const [key, spineInstance] of this.prePositionedSpines.entries()) {
      if (spineInstance?.scale) {
        // Extract symbolId from key format "${symbolIndex}_${symbolId}"
        const symbolId = parseInt(key.split('_')[1], 10);
        const info = symbolData[symbolId];
        const sPay = info?.symbolPay || '';
        const categoryScale = Settings.SYMBOL_SCALE_OVERRIDES?.[sPay]
          ?? Settings.SYMBOL_SCALE_OVERRIDES?.DEFAULT
          ?? 1;
        const symbolScale = (newSymbolHeight / nativeHeight) * categoryScale;
        spineInstance.scale.set(symbolScale);
      }
    }

    for (let i = 0; i < this.overlaySymbolRefs.length; i++) {
      const overlaySymbol = this.overlaySymbolRefs[i];
      if (overlaySymbol) {
        overlaySymbol.y = newSymbolHeight * i;
      }
    }

    // Update coin value text fontSize on resize.
    // Must apply same totalScale counter (spine PIXI scale × bone world scale)
    // as showCoinValueText, since both factors change on resize.
    this.coinValueContainers.forEach((coinData) => {
      if (coinData.bitmapTexts && coinData.bitmapTexts.length > 0) {
        // Compute totalScale from spine + bone (same logic as showCoinValueText)
        let totalScale = 1;
        if (coinData.spineInstance) {
          const spineScale = coinData.spineInstance.scale.x || 1;
          coinData.spineInstance.update(0);
          const valueBone = coinData.spineInstance.skeleton?.findBone("value");
          const boneBaseScale = Math.abs(valueBone?.a) || 1;
          totalScale = spineScale * boneBaseScale;
        }

        const baseFontSize =
          this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
        const adjustedFontSize = totalScale !== 0 ? baseFontSize / totalScale : baseFontSize;

        coinData.bitmapTexts.forEach((bitmapText) => {
          if (bitmapText && !bitmapText.destroyed) {
            bitmapText.fontSize = adjustedFontSize;
            bitmapText.scale.set(1);
            bitmapText.updateText();

            // Recalculate uniform scaling if text overflows
            const coinWidth = this.symbolWidth;
            const paddingPercent = 0.12;
            const maxTextWidth = totalScale !== 0
              ? (coinWidth * (1 - paddingPercent * 2)) / totalScale
              : coinWidth * (1 - paddingPercent * 2);
            const actualTextWidth = bitmapText.width;
            if (actualTextWidth > maxTextWidth) {
              const scale = maxTextWidth / actualTextWidth;
              bitmapText.scale.set(scale);
            }
          }
        });
      }
    });

    // Resize expanding wild spine if initialized
    if (this.expandingWildInitialized) {
      this.applyExpandingWildScaling();
    }
  }

  createSpineInstance(symbolId) {
    const symbolInfo = symbolData[symbolId];
    if (!symbolInfo?.spine || symbolInfo.spine === "") return null;

    const spineName = symbolInfo.spine;
    const skeletonAlias = `${spineName}-skeleton`;
    const atlasAlias = `${spineName}-atlas`;
    const assets = PIXI.Assets.cache;

    if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) return null;

    try {
      const spine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
        autoUpdate: false,
      });

      // Apply skin if feature is enabled and symbol specifies one (shared spine with multiple skins)
      if (
        FEATURES_CONFIG.SPINE_ANIMATIONS.SHARED_SPINE_SKINS &&
        symbolInfo.skin &&
        spine.skeleton
      ) {
        spine.skeleton.setSkinByName(symbolInfo.skin);
        spine.skeleton.setSlotsToSetupPose();
      }

      return spine;
    } catch {
      return null;
    }
  }

  selectSpineAnimation(spineInstance, animationType, symbolId) {
    const cacheKey = `${symbolId}_${animationType}`;
    let cachedAnimation = this.animationCache.get(cacheKey);
    if (cachedAnimation !== undefined) return cachedAnimation;

    if (!spineInstance.skeleton?.data?.animations) {
      this.animationCache.set(cacheKey, null);
      return null;
    }

    const animations = spineInstance.skeleton.data.animations;
    let selectedAnimation = null;

    if (animationType === "auto") {
      selectedAnimation = animations[0]?.name || null;
    } else {
      const exactMatch = animations.find((anim) => {
        const name = anim.name.toLowerCase();
        const type = animationType.toLowerCase();
        // For "win" type, exclude "win_fast" to prevent false matches
        if (type === "win") {
          return name.includes("win") && !name.includes("win_fast");
        }
        return name.includes(type);
      });
      if (exactMatch) {
        selectedAnimation = exactMatch.name;
      } else if (animationType === "win_fast") {
        // win_fast not found → fall back to regular "win"
        const winFallback = animations.find((anim) =>
          anim.name.toLowerCase().includes("win"),
        );
        selectedAnimation = winFallback?.name || animations[0]?.name || null;
      } else if (animationType === "idle") {
        // "static" is the idle-equivalent for spines that don't have an explicit "idle" animation
        // (e.g. jackpot spine j.json: ['land', 'static', 'win'])
        const staticAnim = animations.find((anim) => anim.name === "static");
        selectedAnimation = staticAnim?.name || animations[0]?.name || null;
      } else if (animationType === "land") {
        // "in" is the landing-equivalent for spines that use "in" instead of "land"/"landing"
        const inAnim = animations.find((anim) => anim.name === "in");
        selectedAnimation = inAnim?.name || animations[0]?.name || null;
      } else {
        selectedAnimation = animations[0]?.name || null;
      }
    }

    this.animationCache.set(cacheKey, selectedAnimation);
    return selectedAnimation;
  }

  _smoothTransitionToAnimation(
    spineInstance,
    newAnimationType,
    symbolId,
    loop = true,
  ) {
    if (!spineInstance || !spineInstance.state) return false;

    const newAnimation = this.selectSpineAnimation(
      spineInstance,
      newAnimationType,
      symbolId,
    );
    if (!newAnimation) return false;

    try {
      const currentTracks = spineInstance.state.tracks;
      const hasCurrentAnimation =
        currentTracks && currentTracks.length > 0 && currentTracks[0];

      if (hasCurrentAnimation) {
        const currentTrack = currentTracks[0];
        const currentAnimationName = currentTrack.animation
          ? currentTrack.animation.name
          : null;

        if (currentAnimationName === newAnimation) return true;
        spineInstance.state.addAnimation(0, newAnimation, loop, 0.1);
      } else {
        spineInstance.state.setAnimation(0, newAnimation, loop);
      }
      return true;
    } catch {
      return false;
    }
  }

  async initializeAllSpineInstances() {
    this.createAllPrePositionedSpines();

    // Force-upload all unique symbol atlas textures to GPU.
    // Deferred to next frame so globalThis.__PIXI_RENDERER__ is available.
    // Only run once from the first reel to avoid redundant uploads.
    if (this.reelIndex === 0) {
      requestAnimationFrame(() => {
        const uniqueSpineNames = [
          ...new Set(
            Object.values(symbolData)
              .map((s) => s.spine)
              .filter((s) => s && s !== "")
          ),
        ];
        SpineAtlasUploader.uploadAll(uniqueSpineNames);
      });
    }
  }

  createAllPrePositionedSpines() {
    const WARMUP_POSITION = 0; // Only warm up in first symbol position
    const WARMUP_REEL = 0; // Only warm up in first reel

    for (
      let symbolIndex = 0;
      symbolIndex < GAME_LAYOUT.SYMBOLS_VISIBLE;
      symbolIndex++
    ) {
      const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
      if (!overlaySymbol) continue;

      for (const symbolId of Object.keys(symbolData).map(Number)) {
        // Conditionally exclude blank symbols for Hold and Win mode
        // BLANK_SYMBOL_ID (0): In base game = wild (w1), in Hold and Win = blank space
        const shouldExcludeBlank =
          FEATURES_CONFIG.HOLD_AND_WIN?.ENABLED && symbolId === BLANK_SYMBOL_ID;

        if (shouldExcludeBlank) {
          continue;
        }

        const symbolInfo = symbolData[symbolId];
        // Early check: skip if symbol doesn't exist or has no valid spine
        if (
          !symbolInfo ||
          !symbolInfo.spine ||
          symbolInfo.spine === null ||
          symbolInfo.spine === "" ||
          typeof symbolInfo.spine !== "string"
        ) {
          continue;
        }

        let spineInstance;

        // Only create and warm up spine in first reel, first position
        if (this.reelIndex === WARMUP_REEL && symbolIndex === WARMUP_POSITION) {
          spineInstance = this.createSpineInstance(symbolId);
          if (!spineInstance) continue;

          const scaleData = this.calculateSpineScale(
            spineInstance,
            this.symbolWidth,
            this.symbolHeight,
            true,
            symbolId,
          );

          spineInstance.position.set(scaleData.x, scaleData.y);
          spineInstance.scale.set(scaleData.scale);
          spineInstance.zIndex = 10;
          spineInstance.alpha = 0.001;
          overlaySymbol.addChild(spineInstance);

          // ✅ Build animation availability cache from warmed-up spine
          const key = `${symbolIndex}_${symbolId}`;
          if (spineInstance.skeleton?.data?.animations?.length > 0) {
            const available = new Set(
              spineInstance.skeleton.data.animations.map((a) =>
                a.name.toLowerCase(),
              ),
            );
            // Only cache if there are actual animations
            if (available.size > 0) {
              this.animationAvailability.set(key, available);
            }
          }

          // Warm up animations (shader compilation + GPU buffer upload happens here)
          // Order matters: each setAnimation replaces the previous animation's meshes.
          // "win" is warmed LAST so its meshes survive in the display list after cleanup.
          this.warmUpSpineAnimation(spineInstance, symbolId, "appear");
          this.warmUpSpineAnimation(spineInstance, symbolId, "idle");
          this.warmUpSpineAnimation(spineInstance, symbolId, "land");
          this.warmUpSpineAnimation(spineInstance, symbolId, "win");

          // Schedule cleanup after GPU has rendered all animation meshes (3 frames)
          this.scheduleWarmupCleanup(spineInstance);

          const spineKey = `${symbolIndex}_${symbolId}`;
          this.prePositionedSpines.set(spineKey, spineInstance);
        } else {
          // For all other reels and positions, create fresh instances (shaders already compiled)
          spineInstance = this.createSpineInstance(symbolId);
          if (!spineInstance) continue;

          const scaleData = this.calculateSpineScale(
            spineInstance,
            this.symbolWidth,
            this.symbolHeight,
            true,
            symbolId,
          );

          spineInstance.position.set(scaleData.x, scaleData.y);
          spineInstance.scale.set(scaleData.scale);
          spineInstance.zIndex = 10;
          spineInstance.alpha = 0.001;
          overlaySymbol.addChild(spineInstance);

          // ✅ Build animation availability cache from this spine instance
          // All positions share the same symbol's animations, so use position 0 key for lookup
          const warmupKey = `${WARMUP_POSITION}_${symbolId}`;

          // Check if we already have the cache entry for this symbol (from position 0)
          let available = this.animationAvailability.get(warmupKey);

          // If not, populate it from this spine instance
          if (
            !available &&
            spineInstance.skeleton?.data?.animations?.length > 0
          ) {
            available = new Set(
              spineInstance.skeleton.data.animations.map((a) =>
                a.name.toLowerCase(),
              ),
            );
            // Only cache if there are actual animations
            if (available.size > 0) {
              this.animationAvailability.set(warmupKey, available);
            }
          }

          // Warm up GPU mesh buffers for all animation types (prevents first-use jitter)
          // "win" is warmed LAST so its meshes survive after cleanup
          this.warmUpSpineAnimation(spineInstance, symbolId, "appear");
          this.warmUpSpineAnimation(spineInstance, symbolId, "idle");
          this.warmUpSpineAnimation(spineInstance, symbolId, "land");
          this.warmUpSpineAnimation(spineInstance, symbolId, "win");
          this.scheduleWarmupCleanup(spineInstance);

          const spineKey = `${symbolIndex}_${symbolId}`;
          this.prePositionedSpines.set(spineKey, spineInstance);
        }
      }
    }
  }

  warmUpSpineAnimation(spineInstance, symbolId, animationType = "win") {
    if (!spineInstance?.state) return;
    try {
      const animation = this.selectSpineAnimation(
        spineInstance,
        animationType,
        symbolId,
      );
      if (!animation) return;

      // Use setAnimation (replaces current) instead of clearTracks + addAnimation.
      // This preserves the previous animation's mesh data until the new one replaces it,
      // giving the GPU a chance to render each animation's mesh before it's replaced.
      spineInstance.state.setAnimation(0, animation, false);
      spineInstance.state.update(0.016);
      spineInstance.skeleton.updateWorldTransform();

      spineInstance.alpha = 0.001;
      const overlaySymbol = spineInstance.parent;
      if (overlaySymbol) overlaySymbol.alpha = 0.001;
    } catch {}
  }

  /**
   * Finalize warmup cleanup after GPU has had time to render all animation meshes.
   * Called once per spine AFTER all warmUpSpineAnimation calls complete.
   */
  scheduleWarmupCleanup(spineInstance) {
    if (!spineInstance?.state) return;

    const overlaySymbol = spineInstance.parent;

    // Wait 3 frames — ensures PIXI's renderer has drawn the spine mesh
    // and uploaded vertex/texture data to the GPU for all warmed-up animations
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (spineInstance.state) {
            // Clear tracks after GPU has rendered all warmed-up meshes (3 frames).
            // Mesh GPU buffers are already cached — clearTracks() just resets track state
            // so the first showWinAnimation() starts from a clean slate.
            spineInstance.state.clearTracks();
            spineInstance.autoUpdate = false;
          }
          spineInstance.alpha = 0.001;
          if (overlaySymbol) overlaySymbol.alpha = 0.001;
        });
      });
    });
  }

  forceDeepSpineCleanup() {
    for (const spineInstance of this.prePositionedSpines.values()) {
      if (spineInstance?.state) {
        spineInstance.state.clearTracks();
        spineInstance.autoUpdate = false;
        spineInstance.alpha = 0.001;
      }
    }

    for (let i = 0; i < this.overlaySymbolRefs.length; i++) {
      const overlaySymbol = this.overlaySymbolRefs[i];
      if (overlaySymbol?.alpha > 0) {
        overlaySymbol.alpha = 0.001;
      }
    }

    this.activeAnimations.clear();
  }

  /**
   * Show appear animation at a specific position (used for cascading expanding wilds)
   * @param {number} symbolIndex - Position index (0-3)
   * @param {number} targetSymbolId - Symbol ID to show (typically wild = 0)
   */
  showAppearAnimationAtPosition(symbolIndex, targetSymbolId) {
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (!overlaySymbol) {
      return;
    }

    // Get the pre-positioned spine instance for this position with target symbol
    const spineKey = `${symbolIndex}_${targetSymbolId}`;
    const spineInstance = this.prePositionedSpines.get(spineKey);
    if (!spineInstance) {
      return;
    }

    // Hide any existing animation at this position
    const existingAnimation = this.activeAnimations.get(symbolIndex);
    if (existingAnimation) {
      this.hideWinAnimation(symbolIndex);
    }

    // Make spine and overlay visible
    spineInstance.alpha = 1;
    spineInstance.autoUpdate = true;
    overlaySymbol.alpha = 1;

    // Play appear animation (fallback to land if appear not available)
    let appearAnimation = this.selectSpineAnimation(
      spineInstance,
      "appear",
      targetSymbolId,
    );

    // If no appear animation found, try land as fallback
    if (
      !appearAnimation ||
      appearAnimation === spineInstance.skeleton?.data?.animations?.[0]?.name
    ) {
      const landAnim = this.selectSpineAnimation(
        spineInstance,
        "land",
        targetSymbolId,
      );
      if (landAnim) appearAnimation = landAnim;
    }

    if (spineInstance.state && appearAnimation) {
      // Use setBonesToSetupPose() instead of setToSetupPose() — resets bones without slot attachment changes (no DarkSlotMesh leak)
      spineInstance.skeleton.setBonesToSetupPose();
      spineInstance.state.clearTracks();
      // Play appear animation once (no loop) - will stop at last frame when finished
      spineInstance.state.addAnimation(0, appearAnimation, false, 0);
    }

    // Track this as an active animation
    this.activeAnimations.set(symbolIndex, {
      spineInstance,
      symbolId: targetSymbolId,
      animationType: "appear",
    });

    // Show coin value text if this is a coin symbol
    this.showCoinValueText(symbolIndex, targetSymbolId, spineInstance);
  }

  /**
   * Replace visible symbol animations with target symbol appear animation
   * @param {number} targetSymbolId - Symbol ID to replace visible symbols with
   */
  replaceVisibleSymbols(targetSymbolId) {
    // Use GAME_LAYOUT indices to loop through visible positions
    for (const symbolIndex of GAME_LAYOUT.SYMBOL_INDICES) {
      const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
      if (!overlaySymbol) {
        continue;
      }

      // Get the pre-positioned spine instance for this position with target symbol
      const spineKey = `${symbolIndex}_${targetSymbolId}`;
      const spineInstance = this.prePositionedSpines.get(spineKey);
      if (!spineInstance) {
        continue;
      }

      // Hide any existing animation at this position
      const existingAnimation = this.activeAnimations.get(symbolIndex);
      if (existingAnimation) {
        this.hideWinAnimation(symbolIndex);
      }

      // Make spine and overlay visible
      spineInstance.alpha = 1;
      spineInstance.autoUpdate = true;
      overlaySymbol.alpha = 1;

      // Play appear animation (fallback to land if appear not available)
      let appearAnimation = this.selectSpineAnimation(
        spineInstance,
        "appear",
        targetSymbolId,
      );

      // If no appear animation found, try land as fallback
      if (
        !appearAnimation ||
        appearAnimation === spineInstance.skeleton?.data?.animations?.[0]?.name
      ) {
        const landAnim = this.selectSpineAnimation(
          spineInstance,
          "land",
          targetSymbolId,
        );
        if (landAnim) appearAnimation = landAnim;
      }

      if (spineInstance.state && appearAnimation) {
        spineInstance.state.clearTracks();
        spineInstance.state.addAnimation(0, appearAnimation, true, 0);
      }

      // Track this as an active animation
      this.activeAnimations.set(symbolIndex, {
        spineInstance,
        symbolId: targetSymbolId,
        animationType: "appear",
      });

      // Show coin value text if this is a coin symbol
      this.showCoinValueText(symbolIndex, targetSymbolId, spineInstance);
    }
  }

  /**
   * Create expanding wild spine instance
   */
  createExpandingWildSpine() {
    try {
      if (!this.expandingWildSpineData) {
        return null;
      }

      const assets = PIXI.Assets.cache;
      const skeletonKey = this.expandingWildSpineData.skeleton;
      const atlasKey = this.expandingWildSpineData.atlas;

      if (!assets.has(skeletonKey) || !assets.has(atlasKey)) {
        return null;
      }

      const expandingWildSpine = Spine.from({
        skeleton: skeletonKey,
        atlas: atlasKey,
      });

      if (
        !expandingWildSpine ||
        !expandingWildSpine.skeleton ||
        !expandingWildSpine.state
      ) {
        return null;
      }

      if (
        !expandingWildSpine.skeleton.data ||
        !expandingWildSpine.skeleton.data.animations
      ) {
        return null;
      }

      return expandingWildSpine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Calculate responsive scale and positioning for expanding wild spine animation
   * @param {Spine} spineInstance - The spine instance to scale
   * @param {number} containerWidth - Available container width
   * @param {number} containerHeight - Available container height (full reel height)
   * @returns {Object} - Scale and positioning information
   * Cached for performance - invalidated when dimensions change
   */
  calculateExpandingWildScale(
    spineInstance,
    containerWidth,
    containerHeight,
    preserveAnimation = false,
  ) {
    // Set default animation to get proper bounds (only if not preserving current animation)
    if (
      !preserveAnimation &&
      spineInstance &&
      spineInstance.state &&
      spineInstance.state.data &&
      spineInstance.state.data.skeletonData.animations.length > 0
    ) {
      const defaultAnimation =
        spineInstance.state.data.skeletonData.animations[0];
      spineInstance.state.setAnimation(0, defaultAnimation.name, true);
    }

    // Check cache first
    const cacheKey = `${containerWidth}_${containerHeight}`;
    if (
      this._expandingWildScaleCache &&
      this._expandingWildScaleCache.cacheKey === cacheKey
    ) {
      return this._expandingWildScaleCache.result;
    }

    // Calculate scale to fit within container while maintaining aspect ratio
    const nativeWidth = Settings.SYMBOL_WIDTH;
    const nativeHeight = Settings.SYMBOL_HEIGHT;

    const widthScale = containerWidth / nativeWidth;
    const heightScale = containerHeight / nativeHeight;
    const scale = Math.min(widthScale, heightScale);

    // Calculate final dimensions and centering offsets
    const scaledWidth = nativeWidth * scale;
    const scaledHeight = nativeHeight * scale;
    const x = (containerWidth - scaledWidth) / 2;
    const y = (containerHeight - scaledHeight) / 2;

    const result = {
      scale,
      nativeWidth,
      nativeHeight,
      x,
      y,
      scaledWidth,
      scaledHeight,
    };

    // Cache result
    this._expandingWildScaleCache = {
      cacheKey,
      result,
    };

    return result;
  }

  /**
   * Apply scaling and positioning to expanding wild spine
   */
  applyExpandingWildScaling() {
    if (!this.expandingWildSpine || this.expandingWildSpine.destroyed) {
      return;
    }

    // Get current animation before scaling (to preserve it)
    let currentAnimation = null;
    let currentLoop = false;
    if (this.expandingWildSpine.state) {
      const currentTrack = this.expandingWildSpine.state.getCurrent(0);
      if (currentTrack && currentTrack.animation) {
        currentAnimation = currentTrack.animation.name;
        currentLoop = currentTrack.loop;
      }
    }

    // Calculate full reel height (4 visible symbols)
    const fullReelHeight = this.symbolHeight * GAME_LAYOUT.SYMBOLS_VISIBLE;

    const scaleData = this.calculateExpandingWildScale(
      this.expandingWildSpine,
      this.symbolWidth,
      fullReelHeight,
      true, // Preserve animation during resize
    );

    if (this.expandingWildSpine.scale) {
      this.expandingWildSpine.scale.set(
        scaleData.scale,
        scaleData.scale * this.expandingWildHeightMultiplier,
      );
    }

    if (this.expandingWildSpine.position) {
      const yOffset = fullReelHeight * this.expandingWildYOffsetMultiplier;
      this.expandingWildSpine.position.set(scaleData.x, scaleData.y + yOffset);
    }

    // Restore the animation that was playing before resize
    if (currentAnimation && this.expandingWildSpine.state) {
      try {
        this.expandingWildSpine.state.setAnimation(
          0,
          currentAnimation,
          currentLoop,
        );
      } catch (error) {
        // Silent fail - animation might not exist
      }
    }
  }

  /**
   * Warm up expanding wild spine with all animations (appear, idle, win)
   * Same pattern as warmUpSpineAnimation for symbol spines
   */
  warmUpExpandingWildSpine() {
    if (!this.expandingWildSpine || !this.expandingWildSpine.state) {
      return;
    }

    const animations = ["appear", "idle", "win"];

    animations.forEach((animName) => {
      try {
        // Check if animation exists
        const hasAnim =
          this.expandingWildSpine.skeleton?.data?.animations?.find(
            (anim) => anim.name === animName,
          );

        if (!hasAnim) {
          return;
        }

        // Set animation
        this.expandingWildSpine.state.clearTracks();
        this.expandingWildSpine.state.setAnimation(0, animName, false);

        // CRITICAL: Update one frame to trigger shader compilation
        this.expandingWildSpine.state.update(0.016);

        // CRITICAL: Populate bone/slot hierarchy for GPU
        try {
          if (this.expandingWildSpine.skeleton.physics !== undefined) {
            this.expandingWildSpine.skeleton.updateWorldTransform();
          }
        } catch (transformError) {
          // Skip if physics not initialized
        }

        // Keep at alpha = 0.001 so PixiJS continues rendering it
        this.expandingWildSpine.alpha = 0.001;

        // Clear tracks after warmup frame renders
        requestAnimationFrame(() => {
          if (this.expandingWildSpine?.state) {
            this.expandingWildSpine.state.clearTracks();
            this.expandingWildSpine.alpha = 0.001;
          }
        });
      } catch (error) {
        // Silent fail - warmup is optional optimization
      }
    });
  }

  /**
   * Get available animation names for expanding wild spine
   */
  getExpandingWildAvailableAnimations() {
    if (!this.expandingWildSpine?.skeleton?.data?.animations) {
      return [];
    }
    return this.expandingWildSpine.skeleton.data.animations.map(
      (anim) => anim.name,
    );
  }

  /**
   * Initialize expanding wild spine and add to container
   */
  initializeExpandingWildSpine() {
    if (this.expandingWildInitialized || !this.reelContainer) {
      return;
    }

    this.expandingWildSpine = this.createExpandingWildSpine();

    if (!this.expandingWildSpine) {
      return;
    }

    // Validate spine components
    const missingComponents = [];
    if (!this.expandingWildSpine.skeleton) missingComponents.push("skeleton");
    if (!this.expandingWildSpine.state) missingComponents.push("state");
    if (!this.expandingWildSpine.skeleton?.data)
      missingComponents.push("skeleton.data");
    if (!this.expandingWildSpine.skeleton?.slots)
      missingComponents.push("skeleton.slots");
    if (!this.expandingWildSpine.skeleton?.bones)
      missingComponents.push("skeleton.bones");

    if (missingComponents.length > 0) {
      return;
    }

    // Apply x2 skin for middle reel (index 2) - only if multiplier feature is enabled
    if (
      this.reelIndex === 2 &&
      this.expandingWildSpine.skeleton &&
      FEATURES_CONFIG.EXPANDING_WILDS.MULTIPLIER_ENABLED
    ) {
      try {
        const x2Skin = this.expandingWildSpine.skeleton.data.findSkin("x2");
        if (x2Skin) {
          this.expandingWildSpine.skeleton.setSkin(x2Skin);
          this.expandingWildSpine.skeleton.setSlotsToSetupPose();
        }
      } catch (error) {
        // Fail silently
      }
    }

    this.expandingWildSpine.position.set(0, 0);
    this.expandingWildSpine.zIndex = 15; // Higher than symbol animations
    this.expandingWildSpine.alpha = 0.001; // Hidden initially, keep in render pipeline
    this.isExpandingWildVisible = false;

    this.reelContainer.addChild(this.expandingWildSpine);
    this.applyExpandingWildScaling();

    if (this.expandingWildSpine.parent !== this.reelContainer) {
      return;
    }

    // Minimal warmup - warm up "in" animation since that's what plays first
    this.warmUpExpandingWildSpine();

    this.expandingWildInitialized = true;
  }

  /**
   * Clear all active timeouts for expanding wild animations
   */
  clearExpandingWildTimeouts() {
    this.expandingWildTimeouts.forEach(clearTimeout);
    this.expandingWildTimeouts = [];
  }

  /**
   * Hide expanding wild animation immediately (synchronous, same as regular win animations)
   */
  hideExpandingWildFullReel() {
    if (!this.expandingWildSpine || this.expandingWildSpine.destroyed) {
      this.isExpandingWildVisible = false;
      this.currentExpandingWildAnimation = null;
      return;
    }

    // Synchronous cleanup - same pattern as hideWinAnimation()
    this.expandingWildSpine.alpha = 0.001; // Keep in render pipeline
    this.expandingWildSpine.autoUpdate = false;
    this.isExpandingWildVisible = false;
    this.currentExpandingWildAnimation = null;

    if (this.expandingWildSpine.state) {
      try {
        this.expandingWildSpine.state.clearTracks();
        // Use setBonesToSetupPose() instead of setToSetupPose() — resets bones without slot attachment changes (no DarkSlotMesh leak)
        this.expandingWildSpine.skeleton.setBonesToSetupPose();
      } catch (error) {
        // Silent fail
      }
    }
  }

  /**
   * Force stop expanding wild animations (synchronous, same as regular win animations)
   */
  forceStopExpandingWildFullReel() {
    this.clearExpandingWildTimeouts();
    // Direct synchronous cleanup - same pattern as regular animations
    this.hideExpandingWildFullReel();
  }

  /**
   * Play expanding wild full-reel animation (appear animation, then idle)
   */
  playExpandingWildFullReel() {
    // Lazy initialization if not already initialized
    if (!this.expandingWildInitialized) {
      this.initializeExpandingWildSpine();
    }

    if (
      !this.expandingWildSpine ||
      this.expandingWildSpine.destroyed ||
      !this.expandingWildSpine.state
    ) {
      return;
    }

    requestAnimationFrame(() => {
      if (
        !this.expandingWildSpine ||
        this.expandingWildSpine.destroyed ||
        !this.expandingWildSpine.state
      ) {
        return;
      }

      try {
        this.expandingWildSpine.state.clearTracks();
        this.expandingWildSpine.state.clearListeners();
        this.expandingWildSpine.state.apply(this.expandingWildSpine.skeleton);
        // Use setBonesToSetupPose() instead of setToSetupPose() — resets bones without slot attachment changes (no DarkSlotMesh leak)
        this.expandingWildSpine.skeleton.setBonesToSetupPose();

        this.expandingWildSpine.alpha = 1; // Show
        this.expandingWildSpine.autoUpdate = true;
        this.isExpandingWildVisible = true;

        // Sound is played centrally in useWinController
        // Middle reel (x2 multiplier) plays additional sound when entering idle

        // Play "appear" animation (non-looping), then transition to "idle" (looping)
        this.expandingWildSpine.state.setAnimation(0, "appear", false);
        this.expandingWildSpine.state.addAnimation(0, "idle", true, 0);
        this.currentExpandingWildAnimation = "appear";

        // Add listener for when "appear" completes and "idle" starts
        // Play multiplier sound for middle reel (reel 2 with x2 skin) - only if multiplier feature is enabled
        if (
          this.reelIndex === 2 &&
          FEATURES_CONFIG.EXPANDING_WILDS.MULTIPLIER_ENABLED
        ) {
          const completeListener = {
            complete: (entry) => {
              if (entry.animation.name === "appear") {
                soundManager.play(SOUND_NAME.expanding_wild_multiplier);

                // Remove listener after playing once
                this.expandingWildSpine.state.removeListener(completeListener);
              }
            },
          };
          this.expandingWildSpine.state.addListener(completeListener);
        }
      } catch (error) {
        // Silent fail
        this.hideExpandingWildFullReel();
      }
    });
  }

  /**
   * Play expanding wild win animation (switches from idle to win)
   */
  playExpandingWildFullReelWin() {
    if (
      !this.expandingWildSpine ||
      this.expandingWildSpine.destroyed ||
      !this.expandingWildSpine.state ||
      !this.isExpandingWildVisible
    ) {
      return;
    }

    this._pendingRAF = requestAnimationFrame(() => {
      this._pendingRAF = null;
      if (
        !this.expandingWildSpine ||
        this.expandingWildSpine.destroyed ||
        !this.expandingWildSpine.state
      ) {
        return;
      }

      try {
        // Switch to win animation (looping) - transitions smoothly from current animation
        this.expandingWildSpine.state.setAnimation(0, "win", true);
        this.currentExpandingWildAnimation = "win";
      } catch (error) {
        // Silent fail
      }
    });
  }

  /**
   * Play dark animation for expanding wild (indicates it's not part of current line)
   */
  playExpandingWildDarkAnimation() {
    if (
      !this.expandingWildSpine ||
      this.expandingWildSpine.destroyed ||
      !this.expandingWildSpine.state ||
      !this.isExpandingWildVisible
    ) {
      return;
    }

    try {
      // Switch to dark animation (play once, hold at last frame)
      this.expandingWildSpine.state.setAnimation(0, "dark", false);
      this.currentExpandingWildAnimation = "dark";
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Play win animation for expanding wild (restore normal appearance)
   */
  playExpandingWildWinAnimation() {
    if (
      !this.expandingWildSpine ||
      this.expandingWildSpine.destroyed ||
      !this.expandingWildSpine.state ||
      !this.isExpandingWildVisible
    ) {
      return;
    }

    try {
      // Switch to win animation (looping)
      this.expandingWildSpine.state.setAnimation(0, "win", true);
      this.currentExpandingWildAnimation = "win";
    } catch (error) {
      // Silent fail
    }
  }

  cleanup() {
    // Cancel any pending requestAnimationFrame
    if (this._pendingRAF) {
      cancelAnimationFrame(this._pendingRAF);
      this._pendingRAF = null;
    }

    this.hideAllAnimations();
    this.hideExpandingWildFullReel();
    this.clearExpandingWildTimeouts();

    // Cleanup all coin value texts
    this.hideAllCoinValueTexts();

    for (const spineInstance of this.prePositionedSpines.values()) {
      if (spineInstance) {
        if (spineInstance.state) spineInstance.state.clearTracks();
        if (spineInstance.parent) {
          spineInstance.parent.removeChild(spineInstance);
        }
        spineInstance.destroy({
          children: true,
          texture: false,
          baseTexture: false,
        });
      }
    }

    // Cleanup expanding wild spine
    if (this.expandingWildSpine && !this.expandingWildSpine.destroyed) {
      try {
        if (this.expandingWildSpine.state) {
          this.expandingWildSpine.state.clearTracks();
          this.expandingWildSpine.state.clearListeners();
        }

        if (this.expandingWildSpine.parent) {
          this.expandingWildSpine.parent.removeChild(this.expandingWildSpine);
        }

        this.expandingWildSpine.destroy({
          children: true,
          texture: false,
          baseTexture: false,
        });
      } catch (error) {
        // Silent fail
      }
    }

    this.prePositionedSpines.clear();
    this.activeAnimations.clear();
    this.animationCache.clear();
    this.animationAvailability.clear();
    this.coinValueContainers.clear();
    this.expandingWildSpine = null;
    this.expandingWildInitialized = false;
    this.isExpandingWildVisible = false;
    this.currentExpandingWildAnimation = null;
  }
}

export default OverlayReelController;
